/* eslint-disable no-unused-vars */
import React, { ReactNode } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
} from '@chakra-ui/react';
import { Check, X } from 'phosphor-react';

interface DialogProps extends Omit<ModalProps, 'children'> {
  title?: string;
  description?: string;
  footer?: ReactNode;
  children?: ReactNode;
  hideCloseButton?: boolean;
}

export const Dialog = ({
  title,
  description,
  footer,
  children,
  hideCloseButton = false,
  size = 'lg',
  ...rest
}: DialogProps) => (
  <Modal isCentered autoFocus={false} size={size} scrollBehavior="inside" {...rest}>
    <ModalOverlay />
    <ModalContent>
      {(title || description) && (
        <ModalHeader pb={children ? '16px' : '24px'}>
          {title}
          {description && (
            <Text mt="4px" fontSize="13px" lineHeight="20px" color="content.body" fontWeight={400}>
              {description}
            </Text>
          )}
        </ModalHeader>
      )}
      {!hideCloseButton && <ModalCloseButton />}
      {children && <ModalBody pt={title ? '0' : '24px'}>{children}</ModalBody>}
      {footer && <ModalFooter>{footer}</ModalFooter>}
    </ModalContent>
  </Modal>
);

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
}

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmar ação',
  description = 'Esta ação não pode ser desfeita.',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  isDestructive = false,
  isLoading = false,
}: ConfirmDialogProps) => (
  <Dialog
    isOpen={isOpen}
    onClose={onClose}
    size="md"
    title={title}
    footer={
      <>
        <Button variant="outline" leftIcon={<X size={14} />} onClick={onClose}>
          {cancelLabel}
        </Button>
        <Button
          variant={isDestructive ? 'destructive' : 'solid'}
          leftIcon={<Check size={14} />}
          isLoading={isLoading}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </>
    }
  >
    {typeof description === 'string' ? (
      <Text fontSize="13px" lineHeight="20px" color="content.body">
        {description}
      </Text>
    ) : (
      description
    )}
  </Dialog>
);

interface FormDialogProps extends Omit<ModalProps, 'children'> {
  title: string;
  description?: string;
  children: ReactNode;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  extraActions?: ReactNode;
  submitTestId?: string;
}

export const FormDialog = ({
  title,
  description,
  children,
  onSubmit,
  submitLabel = 'Salvar',
  cancelLabel = 'Cancelar',
  isLoading,
  isDisabled,
  extraActions,
  submitTestId,
  onClose,
  size = 'lg',
  ...rest
}: FormDialogProps) => (
  <Modal
    isCentered
    autoFocus={false}
    size={size}
    scrollBehavior="inside"
    onClose={onClose}
    {...rest}
  >
    <ModalOverlay />
    <ModalContent as="form" noValidate onSubmit={onSubmit}>
      <ModalHeader pb="16px">
        {title}
        {description && (
          <Text mt="4px" fontSize="13px" lineHeight="20px" color="content.body" fontWeight={400}>
            {description}
          </Text>
        )}
      </ModalHeader>
      <ModalCloseButton />
      <ModalBody pt="0" display="flex" flexDirection="column" gap="16px">
        {children}
      </ModalBody>
      <ModalFooter>
        {extraActions}
        <Button type="button" variant="outline" leftIcon={<X size={14} />} onClick={onClose}>
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          leftIcon={<Check size={14} />}
          isLoading={isLoading}
          isDisabled={isDisabled}
          data-testid={submitTestId}
        >
          {submitLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);
